import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, ArrowLeft } from 'lucide-react'
import { apiMethods } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import Button from '../components/Button'

const STAGE_OPTIONS = [
  { value: 'new', label: 'new' },
  { value: 'contacted', label: 'contacted' },
  { value: 'qualified', label: 'qualified' },
  { value: 'disqualified', label: 'disqualified' },
  { value: 'proposal_sent', label: 'proposal_sent' },
  { value: 'won', label: 'won' },
  { value: 'lost', label: 'lost' },
]

const GOOGLE_IMPACT_STAGES = new Set(['qualified', 'won'])
function formatDateTime(value) {
  if (!value) return '-'

  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}

function formatEventStage(event) {
  if (event.stage === 'retracted' && event.retracted_target_stage) {
    return `retracted(${event.retracted_target_stage}; legacy unsupported)`
  }

  return event.stage
}

function stageLabel(stage, showGoogleImpact) {
  return showGoogleImpact && GOOGLE_IMPACT_STAGES.has(stage) ? `${stage} *` : stage
}

function hasGoogleAdsConnection(lead) {
  if (typeof lead?.has_google_ads_connection === 'boolean') {
    return lead.has_google_ads_connection
  }

  return [lead?.gclid, lead?.gbraid, lead?.wbraid].some(
    (value) => typeof value === 'string' && value.trim() !== '',
  )
}

export default function LeadDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isSuperAdmin } = useAuthStore()
  const [formState, setFormState] = useState({
    stage: 'new',
    qualified_value: '',
    won_value: '',
    currency_code: '',
    confirm_currency: false,
    disqualified_reason: '',
    sales_note: '',
  })
  const [submitError, setSubmitError] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['lead', id],
    queryFn: () => apiMethods.getLead(id),
    enabled: !!id,
  })

  const lead = data?.data

  const stageMutation = useMutation({
    mutationFn: (payload) => apiMethods.updateLeadStage(id, payload),
    onSuccess: () => {
      setSubmitError('')
      queryClient.invalidateQueries({ queryKey: ['lead', id] })
      queryClient.invalidateQueries({ queryKey: ['leads'] })
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.errors?.stage?.[0] ||
        error?.response?.data?.errors?.won_value?.[0] ||
        'Failed to update lead stage.'

      setSubmitError(message)
    },
  })

  useEffect(() => {
    if (!lead) return

    setFormState((current) => ({
      ...current,
      stage: lead.current_stage || 'new',
      qualified_value: lead.qualified_value ?? '',
      won_value: lead.won_value ?? '',
      currency_code: lead.currency_code || '',
      confirm_currency: false,
      disqualified_reason: lead.disqualified_reason || '',
      sales_note: lead.sales_note || '',
    }))
  }, [lead])

  if (!isSuperAdmin()) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        You do not have permission to access Leads.
      </div>
    )
  }

  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading lead...</div>
  }

  if (!lead) {
    return <div className="text-sm text-gray-500">Lead not found.</div>
  }

  const leadHasGoogleAdsConnection = hasGoogleAdsConnection(lead)
  const selectedStageImpactsGoogle =
    leadHasGoogleAdsConnection && GOOGLE_IMPACT_STAGES.has(formState.stage)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Button variant="secondary" onClick={() => navigate('/leads')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Leads
          </Button>
          <h1 className="mt-3 text-2xl font-bold text-gray-900">
            {lead.full_name || 'Lead'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">Lead UUID: {lead.lead_uuid}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="rounded-lg bg-white p-5 shadow">
          <h2 className="text-lg font-semibold text-gray-900">Lead Details</h2>
          <dl className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Stage</dt>
              <dd className="font-medium text-gray-900">
                {stageLabel(lead.current_stage, leadHasGoogleAdsConnection)}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Submitted</dt>
              <dd className="text-gray-900">{formatDateTime(lead.submitted_at_utc)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Email</dt>
              <dd className="text-gray-900">{lead.email || '-'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Phone</dt>
              <dd className="text-gray-900">{lead.phone || '-'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Company</dt>
              <dd className="text-gray-900">{lead.company_name || '-'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Project Type</dt>
              <dd className="text-gray-900">{lead.project_type || '-'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Budget Range</dt>
              <dd className="text-gray-900">{lead.budget_range || '-'}</dd>
            </div>
            <div className="space-y-1">
              <dt className="text-gray-500">Comment</dt>
              <dd className="break-words whitespace-pre-wrap text-gray-900">
                {lead.message || '-'}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Ad data consent</dt>
              <dd className="text-gray-900">{lead.ad_user_data_consent ? 'Granted' : 'Denied'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Qualified Value</dt>
              <dd className="text-gray-900">{lead.qualified_value ?? '-'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Won Value</dt>
              <dd className="text-gray-900">{lead.won_value ?? '-'}</dd>
            </div>
          </dl>
        </section>

        <section className="rounded-lg bg-white p-5 shadow">
          <h2 className="text-lg font-semibold text-gray-900">Update Stage</h2>
          <form
            className="mt-4 space-y-3"
            onSubmit={(event) => {
              event.preventDefault()
              stageMutation.mutate({
                stage: formState.stage,
                qualified_value:
                  formState.qualified_value === ''
                    ? undefined
                    : Number(formState.qualified_value),
                won_value:
                  formState.won_value === '' ? undefined : Number(formState.won_value),
                currency_code: formState.confirm_currency
                  ? formState.currency_code || undefined
                  : undefined,
                confirm_currency: formState.confirm_currency || undefined,
                disqualified_reason: formState.disqualified_reason || undefined,
                sales_note: formState.sales_note || undefined,
              })
            }}
          >
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Stage</label>
              <select
                className={`w-full rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 ${
                  selectedStageImpactsGoogle
                    ? 'border-amber-400 bg-amber-50 text-amber-900'
                    : 'border-gray-300'
                }`}
                value={formState.stage}
                onChange={(event) =>
                  setFormState((current) => ({ ...current, stage: event.target.value }))
                }
              >
                {STAGE_OPTIONS.map((stageOption) => (
                  <option key={stageOption.value} value={stageOption.value}>
                    {stageOption.label}
                  </option>
                ))}
              </select>
              {leadHasGoogleAdsConnection ? (
                <p className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-amber-700">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  * = may create Google upload event; not proof of processing or attribution
                </p>
              ) : null}
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Qualified Value</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  value={formState.qualified_value}
                  onChange={(event) =>
                    setFormState((current) => ({
                      ...current,
                      qualified_value: event.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Won Value</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  value={formState.won_value}
                  onChange={(event) =>
                    setFormState((current) => ({
                      ...current,
                      won_value: event.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Currency</label>
              <input
                type="text"
                maxLength={3}
                className="w-full rounded-md border-gray-300 uppercase shadow-sm focus:border-primary-500 focus:ring-primary-500"
                value={formState.currency_code}
                onChange={(event) =>
                  setFormState((current) => ({
                    ...current,
                    currency_code: event.target.value.toUpperCase(),
                  }))
                }
              />
              <label className="mt-2 flex items-start gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  className="mt-0.5 rounded border-gray-300"
                  checked={formState.confirm_currency}
                  onChange={(event) =>
                    setFormState((current) => ({
                      ...current,
                      confirm_currency: event.target.checked,
                    }))
                  }
                />
                <span>
                  Confirm existing and submitted values use this currency. This does not convert amounts.
                </span>
              </label>
              <p className="mt-1 text-xs text-gray-500">
                Verified: {formatDateTime(lead.currency_verified_at)}
              </p>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Disqualified Reason</label>
              <input
                type="text"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                value={formState.disqualified_reason}
                onChange={(event) =>
                  setFormState((current) => ({
                    ...current,
                    disqualified_reason: event.target.value,
                  }))
                }
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Sales Note</label>
              <textarea
                rows={4}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                value={formState.sales_note}
                onChange={(event) =>
                  setFormState((current) => ({ ...current, sales_note: event.target.value }))
                }
              />
            </div>

            {submitError ? <p className="text-sm text-red-600">{submitError}</p> : null}

            <Button type="submit" disabled={stageMutation.isPending}>
              {stageMutation.isPending ? 'Saving...' : 'Save Stage'}
            </Button>
          </form>
        </section>
      </div>

      <section className="rounded-lg bg-white p-5 shadow">
        <h2 className="text-lg font-semibold text-gray-900">Stage Event Upload Log</h2>

        {lead.stage_events?.length ? (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Stage
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Time
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Status
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Upload / diagnostics
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Error
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {lead.stage_events.map((event) => (
                  <tr key={event.id}>
                    <td className="px-3 py-2 text-sm text-gray-900">
                      {formatEventStage(event)}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-600">
                      {formatDateTime(event.stage_at_utc)}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-600">{event.upload_status}</td>
                    <td className="px-3 py-2 text-sm text-gray-600">
                      {event.upload_attempts} / {event.diagnostic_attempts || 0}
                    </td>
                    <td className="px-3 py-2 text-sm text-red-600">
                      {event.provider_error_message || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="mt-4 text-sm text-gray-500">No stage upload events yet.</p>
        )}
      </section>
    </div>
  )
}
